import React from 'react';
import { Box, Grid, TextField, MenuItem, Typography } from '@mui/material';
import FormFieldLabel, { SectionLabel, SubLabel } from '../components/FormFieldLabel';
import ImageUpload from '../components/ImageUpload';
import CoverImageOptimizingBlock from '../components/CoverImageOptimizingBlock';
import EventDescribePrompt from '../components/EventDescribePrompt';
import EventTopicsField from '../components/EventTopicsField';
import MemberMatchingField from '../components/MemberMatchingField';
import {
  mergeFieldSx,
  mergeMultilineFieldSx,
  characterCountSx,
} from '../components/manageEventFieldStyles';
import { EVENT_TYPE_OPTIONS } from '../manageEventForm';
import {
  COVER_IMAGE_ACCEPT,
  COVER_IMAGE_SIZE_HINT,
  COVER_IMAGE_SQUARE_WIDTH,
  EVENT_DESCRIPTION_MAX,
  EVENT_DESCRIPTION_MIN_ROWS,
  EVENT_SHORT_DESCRIPTION_MAX,
  normalizeFormMultilineField,
} from '../eventFormConstants';

const CharacterCount = ({ current, max }) => (
  <Typography component="span" sx={{ ...characterCountSx, display: 'block', mt: 0.5 }}>
    {current}/{max}
  </Typography>
);

const BasicInfoStep = ({
  form,
  errors = {},
  onChange,
  coverImageOptimizing = false,
  coverImageOptimizationError = '',
  onCoverImageSelected,
  onCancelCoverImageOptimization,
  onApplyGeneratedFields,
  showDescribePrompt = false,
}) => {
  const update = (field) => (e) => onChange(field, e.target.value);

  const handleDescriptionChange = (event) => {
    onChange('description', normalizeFormMultilineField(event.target.value, EVENT_DESCRIPTION_MAX));
  };

  const handleShortDescriptionChange = (event) => {
    onChange(
      'wingmanDescription',
      normalizeFormMultilineField(event.target.value, EVENT_SHORT_DESCRIPTION_MAX),
    );
  };

  const showOptimizingBlock = coverImageOptimizing || Boolean(coverImageOptimizationError);

  return (
    <Box>
      {showDescribePrompt && <EventDescribePrompt onApply={onApplyGeneratedFields} />}

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={6}>
          <FormFieldLabel required>Event Title</FormFieldLabel>
          <TextField
            fullWidth
            placeholder="Add Event Title"
            value={form.title}
            onChange={update('title')}
            error={Boolean(errors.title)}
            helperText={errors.title}
            sx={mergeFieldSx(Boolean(errors.title))}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormFieldLabel required>Event Type</FormFieldLabel>
          <TextField
            fullWidth
            select
            value={form.type}
            onChange={update('type')}
            error={Boolean(errors.type)}
            helperText={errors.type}
            sx={mergeFieldSx(Boolean(errors.type))}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="" disabled>
              Select Type
            </MenuItem>
            {EVENT_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Grid container spacing={3} alignItems="stretch" sx={{ mb: 0 }}>
        <Grid item xs={12} md={6}>
          <SectionLabel>Description</SectionLabel>

          <SubLabel required>General</SubLabel>
          <TextField
            fullWidth
            multiline
            minRows={EVENT_DESCRIPTION_MIN_ROWS}
            maxRows={EVENT_DESCRIPTION_MIN_ROWS}
            placeholder="Add Description"
            value={form.description}
            onChange={handleDescriptionChange}
            error={Boolean(errors.description)}
            helperText={errors.description}
            inputProps={{ maxLength: EVENT_DESCRIPTION_MAX }}
            sx={mergeMultilineFieldSx(Boolean(errors.description))}
          />
          <CharacterCount current={form.description.length} max={EVENT_DESCRIPTION_MAX} />

          <SubLabel required sx={{ mt: 2 }}>
            Short Description
          </SubLabel>
          <TextField
            fullWidth
            placeholder="Add Short Description"
            value={form.wingmanDescription}
            onChange={handleShortDescriptionChange}
            error={Boolean(errors.wingmanDescription)}
            helperText={errors.wingmanDescription}
            inputProps={{ maxLength: EVENT_SHORT_DESCRIPTION_MAX }}
            sx={mergeFieldSx(Boolean(errors.wingmanDescription))}
          />
          <CharacterCount
            current={form.wingmanDescription.length}
            max={EVENT_SHORT_DESCRIPTION_MAX}
          />
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {showOptimizingBlock ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <FormFieldLabel required sx={{ mb: 0.5 }}>
                Cover Image
              </FormFieldLabel>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', mb: 1, display: 'block' }}
              >
                {COVER_IMAGE_SIZE_HINT}
              </Typography>
              <CoverImageOptimizingBlock
                error={coverImageOptimizationError}
                onCancel={onCancelCoverImageOptimization}
                onRetry={coverImageOptimizationError ? onCoverImageSelected : undefined}
                accept={COVER_IMAGE_ACCEPT}
              />
            </Box>
          ) : (
            <Box sx={{ width: COVER_IMAGE_SQUARE_WIDTH, maxWidth: COVER_IMAGE_SQUARE_WIDTH }}>
              <ImageUpload
                label="Cover Image"
                required
                stretch
                squareAspect
                accept={COVER_IMAGE_ACCEPT}
                formatHint={COVER_IMAGE_SIZE_HINT}
                preview={form.coverImagePreview}
                error={Boolean(errors.coverImage)}
                helperText={errors.coverImage}
                onImageSelected={onCoverImageSelected}
              />
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <FormFieldLabel required>Total Seats</FormFieldLabel>
          <TextField
            fullWidth
            type="number"
            placeholder="Add Number"
            value={form.totalSeats}
            onChange={update('totalSeats')}
            inputProps={{ min: 1 }}
            error={Boolean(errors.totalSeats)}
            helperText={errors.totalSeats}
            sx={mergeFieldSx(Boolean(errors.totalSeats))}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <EventTopicsField
          value={form.topics}
          onChange={(topics) => onChange('topics', topics)}
          error={Boolean(errors.topics)}
          helperText={errors.topics}
        />
      </Box>

      <Box sx={{ mt: 3 }}>
        <MemberMatchingField checked={form.memberMatching} />
      </Box>
    </Box>
  );
};

export default BasicInfoStep;
